import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Trash2, Phone, MapPin, Calendar, User, Truck, Loader2, Inbox, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const STATUSES = [
  { key: "new", label: "Nuovo", color: "text-amber border-amber/50 bg-amber/10" },
  { key: "in_progress", label: "In gestione", color: "text-signal border-signal/50 bg-signal/10" },
  { key: "closed", label: "Chiuso", color: "text-steel border-edge bg-asphalt" },
];

function statusMeta(key) {
  return STATUSES.find((s) => s.key === key) || STATUSES[0];
}

export default function Admin() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    document.title = "Pannello richieste | EMME SERVICE CAR";
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      if (e.response?.status === 401) {
        logout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Richiesta eliminata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const counts = {
    all: quotes.length,
    new: quotes.filter((q) => q.status === "new").length,
    in_progress: quotes.filter((q) => q.status === "in_progress").length,
    closed: quotes.filter((q) => q.status === "closed").length,
  };
  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-carbon">
      <header className="sticky top-0 z-40 border-b border-edge bg-carbon/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden text-sm font-semibold text-steel sm:block">Pannello richieste</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-steel sm:block">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-md border border-edge px-3.5 py-2 text-xs font-bold uppercase text-ice hover:border-amber hover:text-amber">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-2xl font-extrabold text-ice">Preventivi ricevuti</h1>
          <div className="flex flex-wrap gap-2">
            {[{ key: "all", label: "Tutti" }, ...STATUSES].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`admin-filter-${f.key}`}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase transition-colors ${
                  filter === f.key ? "border-amber bg-amber text-carbon" : "border-edge text-steel hover:text-ice"
                }`}
              >
                {f.label} ({counts[f.key] ?? 0})
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-24 text-steel" data-testid="admin-empty">
            <Inbox className="mb-4 h-12 w-12" />
            <p>Nessuna richiesta {filter !== "all" ? "in questa categoria" : "ricevuta"}.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-quotes-list">
            <AnimatePresence>
              {filtered.map((q) => {
                const meta = statusMeta(q.status);
                const waMsg = encodeURIComponent(`Ciao ${q.name}, la contattiamo da EMME SERVICE CAR per la sua richiesta di trasporto (${q.vehicleType}: ${q.origin} → ${q.destination}).`);
                const waNum = (q.phone || "").replace(/[^0-9]/g, "");
                return (
                  <motion.div
                    key={q.id}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    data-testid={`admin-quote-${q.id}`}
                    className="rounded-xl border border-edge bg-asphalt p-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase ${meta.color}`}>{meta.label}</span>
                          <span className="text-xs text-steel">{new Date(q.createdAt).toLocaleString("it-IT")}</span>
                        </div>
                        <div className="grid gap-2 text-sm sm:grid-cols-2">
                          <p className="flex items-center gap-2 text-ice"><User className="h-4 w-4 text-amber" /> {q.name}</p>
                          <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-ice hover:text-amber"><Phone className="h-4 w-4 text-amber" /> {q.phone}</a>
                          <p className="flex items-center gap-2 text-ice"><Truck className="h-4 w-4 text-amber" /> {q.vehicleType}</p>
                          <p className="flex items-center gap-2 text-ice"><MapPin className="h-4 w-4 text-amber" /> {q.origin} → {q.destination}</p>
                          <p className="flex items-center gap-2 text-ice"><Calendar className="h-4 w-4 text-amber" /> {q.timing || "—"}{q.date ? ` (${q.date})` : ""}</p>
                          {q.email && <p className="truncate text-steel">✉ {q.email}</p>}
                        </div>
                        {(q.condition || q.canLoad || q.notes) && (
                          <div className="mt-3 space-y-1 border-t border-edge pt-3 text-xs text-steel">
                            {q.condition && <p>Condizioni: <span className="text-ice">{q.condition}</span></p>}
                            {q.canLoad && <p>Carico autonomo: <span className="text-ice">{q.canLoad}</span></p>}
                            {q.notes && <p>Note: <span className="text-ice">{q.notes}</span></p>}
                          </div>
                        )}
                      </div>

                      <div className="flex shrink-0 flex-col items-stretch gap-2">
                        <select
                          value={q.status}
                          onChange={(e) => changeStatus(q.id, e.target.value)}
                          data-testid={`admin-status-${q.id}`}
                          className="rounded-md border border-edge bg-carbon px-3 py-2 text-xs text-ice focus:border-amber focus:outline-none"
                        >
                          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                        {waNum && (
                          <a href={`https://wa.me/${waNum}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-md border border-whatsapp/50 px-3 py-2 text-xs font-bold uppercase text-whatsapp hover:bg-whatsapp/10">
                            <MessageCircle className="h-4 w-4" /> WhatsApp
                          </a>
                        )}
                        <button onClick={() => remove(q.id)} data-testid={`admin-delete-${q.id}`} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-destructive/50 px-3 py-2 text-xs font-bold uppercase text-destructive hover:bg-destructive/10">
                          <Trash2 className="h-4 w-4" /> Elimina
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
}
